<template>
    <div class="incentives-details mt-table">
        <slot name="title" v-if="metrics?.length > 0" />
        <table v-if="metrics?.length > 0">
            <thead>
                <tr>
                    <th data-field="metricName">Metric</th>
                    <th data-field="target">Target</th>
                    <th data-field="status">Status</th>
                    <th data-field="previousTarget">Previous Target</th>
                    <th data-field="actual">Actual</th>
                    <th data-field="weight">Weight</th>
                    <th data-field="performanceCycleNote">Note</th>
                </tr>
            </thead>
            <tbody v-for="group in groups" :key="group.name">
                <tr v-for="(metric, index) in group.metrics" :key="`${group.name}-${index}`">
                    <td data-field="metricName">{{ metric.metricName }}</td>
                    <td data-field="target" :title="metric.target?.text">{{ formatValue(metric.target) }}</td>
                    <td data-field="status">{{ metric.status }}</td>
                    <td data-field="previousTarget" :title="metric.previousTarget?.text">
                        {{ formatValue(metric.previousTarget) }}
                    </td>
                    <td data-field="actual" :title="metric.actual?.text">
                        {{ formatValue(metric.actual) }}
                    </td>
                    <td data-field="weight">{{ formatWeight(metric.weight) }}</td>
                    <td data-field="performanceCycleNote">{{ metric.performanceCycleNote }}</td>
                </tr>
            </tbody>
        </table>
    </div>
</template>
<script setup>
import { groupBy } from '@/utils/array.js'
import { formatValue } from '@/services/incentivesService.js'
import { computed } from 'vue'

const props = defineProps({
    showHistoricalHitRate: {
        type: Boolean,
        required: false,
        default: false,
    },
    metrics: {
        type: Array,
        required: true,
    },
})

function byWeight(lhs, rhs) {
    const result = (rhs.weight ?? 0) - (lhs.weight ?? 0)

    if (result !== 0) {
        return result
    }

    return lhs.metricName.localeCompare(rhs.metricName)
}

const groups = computed(() => {
    const grouped = groupBy(props.metrics, m => m.performanceCycle ?? 'other')
    return [
        grouped.past ? { name: 'Past', metrics: grouped.past.sort(byWeight) } : null,
        grouped['past and future']
            ? { name: 'Past and Future', metrics: grouped['past and future'].sort(byWeight) }
            : null,
        grouped.future ? { name: 'Current', metrics: grouped.future.sort(byWeight) } : null,
        grouped.dropped ? { name: 'Dropped', metrics: grouped.dropped.sort(byWeight) } : null,
        grouped.other ? { name: 'Other', metrics: grouped.other.sort(byWeight) } : null,
    ].filter(g => g)
})

function formatWeight(value) {
    if (!value) {
        return '-'
    }

    return (value / 100).toLocaleString([], { style: 'percent', maximumFractionDigits: 0 })
}
</script>
<style scoped>
.incentives-details {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

table {
    table-layout: fixed;
}

th {
    line-height: 1;
}

[data-field='metricName'] {
    width: 40%;
}

[data-field='target'],
[data-field='previousTarget'],
[data-field='actual'] {
    width: 20%;
}

[data-field='status'] {
    width: 120px;
}

[data-field='weight'] {
    width: 80px;
}

td[data-field='target'],
td[data-field='previousTarget'],
td[data-field='weight'] {
    text-align: right;
}

.stip {
    & [data-field='performanceCycleNote'] {
        display: none;
    }
}

.ltip {
    & [data-field='actual'],
    & [data-field='weight'] {
        display: none;
    }

    & [data-field='metricName'] {
        width: 40%;
    }

    & [data-field='target'],
    & [data-field='previousTarget'],
    & [data-field='performanceCycleNote'] {
        width: 20%;
    }
}
</style>
